import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { merge } from 'lodash-es'
import type { MarkOptional, DeepPartial } from 'ts-essentials'

import type {
  Status,
  Room,
  RootRoomActivity,
  RoomActivity,
  RoomActivityGroup,
  RoomRelation,
  MediaGroupId,
  ActivityId,
  ActivityGroupId,
  RoomId,
  RoomActivityId,
  RoomActivityGroupId,
  RoomRelationId,
} from '@/types'

import type { Options } from './BaseService.types'
import BaseService from './BaseService'
import type { RoomFilter, ExistingGame } from './RoomService.types'
import type RoomActivityService from './RoomActivityService'
import type RoomActivityGroupService from './RoomActivityGroupService'

export default class RoomService extends BaseService<
  Room,
  Partial<Room>,
  RoomFilter
> {
  constructor(
    client: AxiosInstance,
    private readonly roomActivityService: RoomActivityService,
    private readonly roomActivityGroupService: RoomActivityGroupService
  ) {
    super(client, 'room')
  }

  get<R extends DeepPartial<Room> = Room>(
    id: RoomId,
    config?: AxiosRequestConfig & Options
  ): Promise<R> {
    const merged = config ?? {}
    if (config?.relations === true) {
      return this.client.get<R>(`/room/${id}/view`).then(({ data }) => data)
    }
    return super.get(id, merged)
  }

  async copy(
    id: RoomId,
    name?: string,
    existing?: ExistingGame
  ): Promise<RoomId> {
    const { data } = await this.client.post<RoomId>(`/${this.entity}/${id}`, {
      name,
      existing,
    })
    return data
  }

  /** Downloads the full room tree — the `GET /room/:id/view` payload. */
  async export(id: RoomId): Promise<Room> {
    return this.get(id, { relations: true })
  }

  /** Creates a new room from a previously exported room JSON. */
  async import(room: Room): Promise<RoomId> {
    const { data } = await this.client.post<{ id: RoomId }>('/room/import', {
      room,
    })
    return data.id
  }

  byStatus<R extends Partial<Room> = Room>(
    status: Status,
    config?: AxiosRequestConfig
  ): Promise<R[]> {
    const params = { s: { status: { $eq: status } } }
    const merged = merge({}, { params }, config)
    return this.list<R>(merged)
  }

  addActivity(
    roomId: RoomId,
    activityId: ActivityId,
    position: number
  ): Promise<RootRoomActivity> {
    return this.roomActivityService.create({
      roomId,
      activityId,
      position,
    })
  }

  removeActivity(relationId: RoomActivityId): Promise<RoomActivityId> {
    return this.roomActivityService.delete(relationId)
  }

  async addActivityGroup(
    roomId: RoomId,
    activityGroupId: number,
    position: number
  ): Promise<RoomActivityGroup> {
    return this.roomActivityGroupService.create({
      roomId,
      activityGroupId,
      position,
    })
  }

  async removeActivityGroup(
    relationId: RoomActivityGroupId
  ): Promise<RoomActivityGroupId> {
    return this.roomActivityGroupService.delete(relationId)
  }

  async updateActivity(
    ...args: Parameters<RoomActivityService['updateActivity']>
  ): ReturnType<RoomActivityService['updateActivity']> {
    return this.roomActivityService.updateActivity(...args)
  }

  async addActivityGroupActivity(
    ...args: Parameters<RoomActivityGroupService['addActivity']>
  ): ReturnType<RoomActivityGroupService['addActivity']> {
    return this.roomActivityGroupService.addActivity(...args)
  }

  async updateActivityGroupActivity(
    ...args: Parameters<RoomActivityGroupService['updateActivity']>
  ): ReturnType<RoomActivityGroupService['updateActivity']> {
    return this.roomActivityGroupService.updateActivity(...args)
  }

  async removeActivityGroupActivity(
    ...args: Parameters<RoomActivityGroupService['removeActivity']>
  ): ReturnType<RoomActivityGroupService['removeActivity']> {
    return this.roomActivityGroupService.removeActivity(...args)
  }

  async updateActivityPosition(
    relationId: RoomActivityId,
    position: number
  ): Promise<RoomActivity>
  async updateActivityPosition(payload: {
    relationId: RoomActivityId
    position: number
  }): Promise<RoomActivity>
  async updateActivityPosition(...args: any[]) {
    if (typeof args[0] === 'object') {
      return this.roomActivityService.update({
        id: args[0].relationId,
        position: args[0].position,
      })
    }
    return this.roomActivityService.update({ id: args[0], position: args[1] })
  }

  async updateActivityGroupPosition(
    relationId: RoomActivityGroupId,
    position: number
  ): Promise<RoomActivityGroup>
  async updateActivityGroupPosition(payload: {
    relationId: RoomActivityGroupId
    position: number
  }): Promise<RoomActivityGroup>
  async updateActivityGroupPosition(...args: any[]) {
    if (typeof args[0] === 'object') {
      return this.roomActivityGroupService.update({
        id: args[0].relationId,
        position: args[0].position,
      })
    }
    return this.roomActivityGroupService.update({
      id: args[0],
      position: args[1],
    })
  }

  async link(
    relation: MarkOptional<Omit<RoomRelation, 'id'>, 'settings'>
  ): Promise<RoomRelation> {
    const { data } = await this.client.post<RoomRelation>(
      '/room-relation',
      this.normalize(relation)
    )
    return data
  }

  async unlink(roomRelationId: RoomRelationId): Promise<void> {
    await this.client.delete(`/room-relation/${roomRelationId}`)
  }

  async linkBulk(
    payload: MarkOptional<Omit<RoomRelation, 'id'>, 'settings'>[]
  ): Promise<RoomRelation[]> {
    const { data } = await this.client.post<RoomRelation[]>(
      '/room-relation/bulk',
      {
        bulk: payload.map(this.normalize),
      }
    )
    return data
  }

  async updateLink(
    roomRelationId: number,
    payload: Partial<Pick<RoomRelation, 'right' | 'settings'>>
  ): Promise<RoomRelation> {
    const { data } = await this.client.patch<RoomRelation>(
      `/room-relation/${roomRelationId}`,
      this.normalize(payload)
    )
    return data
  }

  async getActivityRelation(
    ...args: Parameters<RoomActivityService['get']>
  ): Promise<RoomActivity> {
    return this.roomActivityService.get(...args)
  }

  async getActivityGroupRelation(
    ...args: Parameters<RoomActivityGroupService['get']>
  ): Promise<RoomActivityGroup> {
    return this.roomActivityGroupService.get(...args)
  }

  async addActivityGroups(id: RoomId, ids: ActivityGroupId[]): Promise<void> {
    await this.client.post(`room/${id}/activity-groups`, { ids })
  }

  async addActivities(id: RoomId, ids: ActivityId[]): Promise<void> {
    await this.client.post(`room/${id}/activities`, { ids })
  }

  async applyMediaGroup(id: RoomId, group: MediaGroupId): Promise<void> {
    await this.client.put(`/room/${id}/media-group`, {
      id: group,
    })
  }

  private normalize<T extends Partial<RoomRelation>>(relation: Partial<T>) {
    return {
      ...relation,
      ...(relation.left !== undefined
        ? relation.left.type === 'Activity'
          ? { leftActivityId: relation.left.id }
          : { leftActivityGroupId: relation.left.id }
        : null),
      ...(relation.right !== undefined
        ? relation.right.type === 'Activity'
          ? { rightActivityId: relation.right.id }
          : { rightActivityGroupId: relation.right.id }
        : null),
    }
  }

  protected s(filter: RoomFilter, params: Record<string, unknown>) {
    if (filter.status !== undefined) {
      params.status = { $eq: filter.status }
    }
    if (filter.id !== undefined) {
      params.id = { $in: filter.id }
    }
  }

  static MEDIA_RELATION = 'media'
  static RELATIONS_RELATION = 'relations'
  static ACTIVITIES_RELATION = 'activities'
  static ACTIVITIES_ACTIVITY_RELATION = 'activities.activity'
  static ACTIVITIES_ACTIVITY_MEDIA_RELATION = 'activities.activity.media'
  static ACTIVITIES_ACTIVITY_MEDIA_MEDIA_RELATION =
    'activities.activity.media.media'
  static ACTIVITIES_ACTIVITY_STEPS_RELATION = 'activities.activity.steps'
  static ACTIVITIES_ACTIVITY_STEPS_MEDIA_RELATION =
    'activities.activity.steps.media'
  static ACTIVITIES_ACTIVITY_STEPS_MEDIA_MEDIA_RELATION =
    'activities.activity.steps.media.media'
  static ACTIVITY_GROUPS_RELATION = 'activityGroups'
  static MEDIA_MEDIA_RELATION = 'media.media'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_RELATION =
    'activityGroups.activityGroup'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_RELATION =
    'activityGroups.activityGroup.activities'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_RELATION =
    'activityGroups.activityGroup.activities.activity'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_RELATION =
    'activityGroups.activityGroup.activities.activity.media'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_MEDIA_MEDIA_RELATION =
    'activityGroups.activityGroup.activities.activity.media.media'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_RELATION =
    'activityGroups.activityGroup.activities.activity.steps'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_RELATION =
    'activityGroups.activityGroup.activities.activity.steps.media'
  static ACTIVITY_GROUPS_ACTIVITY_GROUP_ACTIVITIES_ACTIVITY_STEPS_MEDIA_MEDIA_RELATION =
    'activityGroups.activityGroup.activities.activity.steps.media.media'
}
